import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, User, Mail, Calendar, Edit2, X, MapPin, Briefcase, Building, Phone, Lock, Eye, EyeOff, Trash2, AlertTriangle } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { Button, useToast, LoadingSpinner, LineChart } from '../../components/common';
import { updateProfile, changePassword, deleteAccount } from '../../store/auth/authSlice';
import { UserRole, Zone, ZoneLabels } from '../../types';
import { readingSessionsService } from '../../services/api/reading-sessions.service';
import { ReadingSessionSummary } from '../../types/reading-session.types';

export const ProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, isLoading } = useAppSelector((state) => state.auth);

  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);

  // Form state
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [campus, setCampus] = useState(user?.campus || '');
  const [zone, setZone] = useState<Zone | ''>(user?.zone || '');
  const [title, setTitle] = useState(user?.title || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const [stats, setStats] = useState<ReadingSessionSummary | null>(null);
  const [statsGroupBy, setStatsGroupBy] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const loadReadingStats = useCallback(async () => {
    if (!user || user.role !== UserRole.CONSUMER) {
      return;
    }

    try {
      setStatsLoading(true);
      const data = await readingSessionsService.getSummary({ groupBy: statsGroupBy, userId: user._id, limit: 20 });
      setStats(data);
      setStatsError(null);
    } catch (err: any) {
      setStatsError(err.response?.data?.message || 'Unable to load your reading stats');
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [user, statsGroupBy]);

  // Reset form when user data changes
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setCampus(user.campus || '');
      setZone(user.zone || '');
      setTitle(user.title || '');
      setPhoneNumber(user.phoneNumber || '');
    }
  }, [user]);

  useEffect(() => {
    loadReadingStats();
  }, [loadReadingStats]);

  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return [hrs > 0 ? `${hrs}h` : '', mins > 0 ? `${mins}m` : '', `${secs}s`]
      .filter(Boolean)
      .join(' ');
  };

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setProfileImage(null);
    setProfilePreview(null);
    // Reset form to current user values
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setCampus(user.campus || '');
      setZone(user.zone || '');
      setTitle(user.title || '');
      setPhoneNumber(user.phoneNumber || '');
    }
  };

  const handleSaveProfile = async () => {
    try {
      const updateData: {
        profileImage?: File;
        firstName?: string;
        lastName?: string;
        campus?: string;
        zone?: string;
        title?: string;
        phoneNumber?: string;
      } = {};

      // Only include changed fields
      if (profileImage) {
        updateData.profileImage = profileImage;
      }
      if (firstName !== user?.firstName) {
        updateData.firstName = firstName;
      }
      if (lastName !== user?.lastName) {
        updateData.lastName = lastName;
      }
      if (phoneNumber !== (user?.phoneNumber || '')) {
        updateData.phoneNumber = phoneNumber;
      }
      if (user?.role === UserRole.CONSUMER) {
        if (campus !== (user?.campus || '')) {
          updateData.campus = campus;
        }
        if (zone !== (user?.zone || '')) {
          updateData.zone = zone || undefined;
        }
      }
      if (user?.role === UserRole.ADMIN) {
        if (title !== (user?.title || '')) {
          updateData.title = title;
        }
      }

      // Only make API call if there are changes
      if (Object.keys(updateData).length > 0) {
        await dispatch(updateProfile(updateData)).unwrap();
        showToast('Profile updated successfully', 'success');
      }

      setIsEditing(false);
      setProfileImage(null);
      setProfilePreview(null);
    } catch {
      showToast('Failed to update profile', 'error');
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      await dispatch(changePassword({ currentPassword, newPassword })).unwrap();
      showToast('Password changed successfully', 'success');
      setIsChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error: any) {
      setPasswordError(error || 'Failed to change password');
    }
  };

  const handleCancelPasswordChange = () => {
    setIsChangingPassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordError('');
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Please enter your password to confirm');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      await dispatch(deleteAccount(deletePassword)).unwrap();
      showToast('Account deleted successfully', 'success');
      navigate('/login');
    } catch (error: any) {
      setDeleteError(error || 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteError('');
  };

  const displayImage = profilePreview || user?.profileImage;
  const isConsumer = user?.role === UserRole.CONSUMER;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">My Profile</h1>
          <p className="text-sm sm:text-base text-slate-500">
            {isEditing ? 'Edit your profile information' : 'View and update your profile information'}
          </p>
        </div>
        {!isEditing && (
          <Button variant="secondary" onClick={() => setIsEditing(true)}>
            <Edit2 size={16} className="mr-2" />
            Edit
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
        {/* Profile Header with Avatar */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-8 sm:py-12 text-center">
          <div className="relative inline-block">
            <label className={`${isEditing ? 'cursor-pointer' : ''} group`}>
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden border-4 border-white/50 shadow-xl mx-auto">
                {displayImage ? (
                  <img src={displayImage} alt={user?.firstName} className="w-full h-full object-cover" />
                ) : (
                  <User size={48} className="text-white/80" />
                )}
              </div>
              {isEditing && (
                <>
                  <div className="absolute bottom-1 right-1 w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:bg-primary-50 transition-colors">
                    <Camera size={18} className="text-primary-600" />
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    className="hidden"
                  />
                </>
              )}
            </label>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mt-4">
            {user?.firstName} {user?.lastName}
          </h2>
          <p className="text-primary-100 capitalize">{user?.role}</p>
        </div>

        {/* Profile Details / Edit Form */}
        <div className="p-6 sm:p-8 space-y-6">
          {isEditing ? (
            // Edit Mode
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              {isConsumer ? (
                // Consumer-specific fields
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Campus
                    </label>
                    <input
                      type="text"
                      value={campus}
                      onChange={(e) => setCampus(e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="e.g. Legon"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Zone
                    </label>
                    <select
                      value={zone}
                      onChange={(e) => setZone(e.target.value as Zone | '')}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white"
                    >
                      <option value="">Select Zone</option>
                      {Object.values(Zone).map((z) => (
                        <option key={z} value={z}>
                          {ZoneLabels[z]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                // Admin-specific fields
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="e.g. Regional Coordinator, Zone Leader"
                  />
                </div>
              )}

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="e.g. +233 XX XXX XXXX"
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed"
                />
                <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-slate-100">
                <Button variant="secondary" onClick={handleCancelEdit} className="w-full sm:w-auto">
                  <X size={16} className="mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSaveProfile} isLoading={isLoading} className="w-full sm:w-auto">
                  Save Changes
                </Button>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-primary-50 rounded-xl">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <User size={20} className="text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Full Name</p>
                  <p className="font-medium text-slate-900">{user?.firstName} {user?.lastName}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-primary-50 rounded-xl">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <Mail size={20} className="text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Email Address</p>
                  <p className="font-medium text-slate-900">{user?.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-primary-50 rounded-xl">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <Phone size={20} className="text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Phone Number</p>
                  <p className="font-medium text-slate-900">{user?.phoneNumber || '-'}</p>
                </div>
              </div>

              {isConsumer ? (
                // Consumer-specific display
                <>
                  <div className="flex items-center gap-4 p-4 bg-primary-50 rounded-xl">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <Building size={20} className="text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Campus</p>
                      <p className="font-medium text-slate-900">{user?.campus || '-'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-primary-50 rounded-xl">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <MapPin size={20} className="text-primary-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Zone</p>
                      <p className="font-medium text-slate-900">
                        {user?.zone ? ZoneLabels[user.zone] : '-'}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                // Admin-specific display
                <div className="flex items-center gap-4 p-4 bg-primary-50 rounded-xl">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Briefcase size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Title</p>
                    <p className="font-medium text-slate-900">{user?.title || '-'}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 p-4 bg-primary-50 rounded-xl">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <Calendar size={20} className="text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Member Since</p>
                  <p className="font-medium text-slate-900">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : '-'}
                  </p>
                </div>
              </div>

              {isConsumer && !isEditing && (
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">My Reading Stats</h2>
                      <p className="text-sm text-slate-500">View your own reading activity and progress.</p>
                    </div>
                    <div className="min-w-[180px]">
                      <label className="block text-sm text-slate-500 mb-1">Group by</label>
                      <select
                        value={statsGroupBy}
                        onChange={(e) => setStatsGroupBy(e.target.value as any)}
                        className="w-full rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-primary-200"
                      >
                        <option value="day">Day</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                        <option value="year">Year</option>
                      </select>
                    </div>
                  </div>

                  {statsLoading ? (
                    <div className="flex justify-center py-10">
                      <LoadingSpinner size="lg" />
                    </div>
                  ) : statsError ? (
                    <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                      {statsError}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-3">
                        <div className="rounded-3xl bg-slate-50 p-4 text-center">
                          <p className="text-sm text-slate-500">Total Reading Time</p>
                          <p className="mt-4 text-2xl font-semibold text-slate-900">{stats ? formatDuration(stats.totalDurationSeconds) : '0s'}</p>
                        </div>
                        <div className="rounded-3xl bg-slate-50 p-4 text-center">
                          <p className="text-sm text-slate-500">Total Sessions</p>
                          <p className="mt-4 text-2xl font-semibold text-slate-900">{stats?.sessions ?? 0}</p>
                        </div>
                        <div className="rounded-3xl bg-slate-50 p-4 text-center">
                          <p className="text-sm text-slate-500">Data Points</p>
                          <p className="mt-4 text-2xl font-semibold text-slate-900">{stats?.summary?.length ?? 0}</p>
                        </div>
                      </div>

                      <div className="min-h-[220px]">
                        {stats?.summary?.length ? (
                          <LineChart
                            data={stats.summary.map((row) => ({ label: row.period, value: Math.round(row.totalDurationSeconds / 60) }))}
                            className="h-[220px]"
                          />
                        ) : (
                          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500">
                            Your reading activity will appear here after you start reading.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Change Password Section */}
              <div className="pt-4 border-t border-slate-200">
                {isChangingPassword ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <Lock size={20} className="text-primary-600" />
                      Change Password
                    </h3>

                    {passwordError && (
                      <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                        {passwordError}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors pr-12"
                          placeholder="Enter current password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors pr-12"
                          placeholder="Enter new password (min 6 characters)"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder="Confirm new password"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
                      <Button variant="secondary" onClick={handleCancelPasswordChange} className="w-full sm:w-auto">
                        <X size={16} className="mr-2" />
                        Cancel
                      </Button>
                      <Button onClick={handleChangePassword} isLoading={isLoading} className="w-full sm:w-auto">
                        Update Password
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    <Lock size={18} />
                    Change Password
                  </button>
                )}
              </div>

              {/* Delete Account Section */}
              <div className="pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium transition-colors"
                >
                  <Trash2 size={18} />
                  Delete Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Delete Account</h3>
                <p className="text-sm text-slate-500">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-slate-600 mb-4">
              Are you sure you want to delete your account? All your data will be permanently removed.
            </p>

            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                {deleteError}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Enter your password to confirm
              </label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                placeholder="Enter your password"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="secondary"
                onClick={handleCancelDelete}
                className="w-full sm:w-auto flex-1"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="w-full sm:w-auto flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
